/**
 * Converts Markdown formatted text to HTML
 * Supports headers, bold, italic, lists, and paragraphs
 * 
 * @param {string} markdown - The markdown text to convert
 * @returns {string} - HTML formatted string
 */
export const convertMarkdownToHtml = (markdown) => {
  if (!markdown) return "";
  
  let html = markdown;
  
  // Convert headers (## and ###)
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  
  // Convert bold text (**text** or __text__)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // Convert italic text (*text* or _text_)
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');
  
  // Convert bullet lists
  // First, identify list blocks
  const lines = html.split('\n');
  let inList = false;
  let processedLines = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    // Check if line is a list item
    if (trimmedLine.startsWith('- ')) {
      if (!inList) {
        processedLines.push('<ul>');
        inList = true;
      }
      const listContent = trimmedLine.substring(2).trim();
      processedLines.push(`<li>${listContent}</li>`);
    } else {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(line);
    }
  }
  
  // Close any open list
  if (inList) {
    processedLines.push('</ul>');
  }
  
  html = processedLines.join('\n');
  
  // Convert line breaks to paragraphs
  // Split by double newlines for paragraphs
  const paragraphs = html.split('\n\n');
  html = paragraphs.map(para => {
    const trimmed = para.trim();
    // Don't wrap if already wrapped in tags
    if (trimmed.startsWith('<h') || trimmed.startsWith('<ul') || 
        trimmed.startsWith('<ol') || trimmed === '') {
      return trimmed;
    }
    // Don't wrap individual lines within lists or headers
    if (trimmed.includes('<li>') || trimmed.includes('</ul>')) {
      return trimmed;
    }
    return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
  }).join('\n');
  
  // Clean up extra newlines
  html = html.replace(/\n{3,}/g, '\n\n');
  
  return html;
};

/**
 * Converts HTML back to Markdown (optional - for reverse conversion)
 * 
 * @param {string} html - The HTML text to convert
 * @returns {string} - Markdown formatted string
 */
export const convertHtmlToMarkdown = (html) => {
  if (!html) return "";
  
  let markdown = html;
  
  // Convert headers
  markdown = markdown.replace(/<h2>(.+?)<\/h2>/g, '## $1');
  markdown = markdown.replace(/<h3>(.+?)<\/h3>/g, '### $1');
  
  // Convert bold
  markdown = markdown.replace(/<strong>(.+?)<\/strong>/g, '**$1**');
  markdown = markdown.replace(/<b>(.+?)<\/b>/g, '**$1**');
  
  // Convert italic
  markdown = markdown.replace(/<em>(.+?)<\/em>/g, '*$1*');
  markdown = markdown.replace(/<i>(.+?)<\/i>/g, '*$1*');
  
  // Convert lists
  markdown = markdown.replace(/<ul>/g, '');
  markdown = markdown.replace(/<\/ul>/g, '');
  markdown = markdown.replace(/<li>(.+?)<\/li>/g, '- $1');
  
  // Convert paragraphs
  markdown = markdown.replace(/<p>(.+?)<\/p>/g, '$1\n\n');
  markdown = markdown.replace(/<br\s*\/?>/g, '\n');
  
  // Clean up extra newlines
  markdown = markdown.replace(/\n{3,}/g, '\n\n');
  
  return markdown.trim();
};